import { useEffect } from 'react';
import { Droplets, Waves, Sprout, Fish, Wind, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, ecosystemComponents } from '../mock/data';

const LOGO_VIDEO_URL = process.env.REACT_APP_LOGO_VIDEO_URL;

export const LivingWater = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const ecosystemLayers = [
    {
      title: "The Water Column",
      description: "Moving water is living water. Pumps and filtration create circulation, preventing stagnation and maintaining healthy oxygen levels for all aquatic life.",
      icon: Waves,
      details: ["Continuous circulation", "Oxygenation", "Temperature regulation", "Prevents algae buildup"]
    },
    {
      title: "Aquatic Plant Life",
      description: "Plants are nature's filtration system. They absorb excess nutrients, produce oxygen, provide shade, and create habitat for beneficial organisms.",
      icon: Sprout,
      details: ["Natural filtration", "Oxygen production", "Nutrient absorption", "Wildlife habitat"]
    },
    {
      title: "Biological Balance",
      description: "Beneficial bacteria colonize rocks, gravel, and filter media, breaking down fish waste and organic matter into harmless compounds.",
      icon: Droplets,
      details: ["Breaks down waste", "Maintains water clarity", "Natural biological filter", "Colonizes on rocks & media"]
    },
    {
      title: "Fish & Wildlife",
      description: "Koi and goldfish complete the ecosystem, adding movement, color, and life while contributing to the natural balance through their interaction with plants and bacteria.",
      icon: Fish,
      details: ["Natural pest control", "Nutrient cycling", "Visual beauty", "Living ecosystem"]
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="pt-32 pb-24 px-6 lg:px-12 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light mb-8 tracking-tight animate-fade-in-up">
            Living Water<br />Environments
          </h1>
          <p className="text-xl text-stone-300 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            More than just water and rocks—a complete, self-sustaining ecosystem that mimics nature's perfect balance.
          </p>
        </div>
      </section>

      {/* Brand Animation */}
      <section className="py-12 px-6 lg:px-12 bg-white fade-in-section">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-sm overflow-hidden shadow-lg">
            <video
              className="w-full h-auto"
              autoPlay
              playsInline
            >
              <source src={LOGO_VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* What Makes It "Living" */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6 tracking-tight">
              What Makes Water "Living"?
            </h2>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
              The difference between a stagnant pool and a thriving ecosystem lies in five interconnected elements working together in harmony.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ecosystemLayers.map((layer, index) => {
              const IconComponent = layer.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-white rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-medium text-stone-800 mb-4">{layer.title}</h3>
                  <p className="text-stone-600 mb-6 leading-relaxed">{layer.description}</p>
                  <ul className="space-y-2">
                    {layer.details.map((detail, i) => (
                      <li key={i} className="text-sm text-stone-500 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full"></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Example */}
      <section className="py-24 bg-white fade-in-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-light text-stone-800 mb-6 tracking-tight">
                See The Ecosystem in Action
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
                    <Droplets className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800 mb-2">Clear, Healthy Water</h4>
                    <p className="text-stone-600 text-sm">Natural biological filtration keeps water crystal clear without harsh chemicals.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
                    <Fish className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800 mb-2">Thriving Fish</h4>
                    <p className="text-stone-600 text-sm">Koi and goldfish add color and movement while participating in the ecosystem balance.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
                    <Sprout className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h4 className="font-medium text-stone-800 mb-2">Lush Plant Growth</h4>
                    <p className="text-stone-600 text-sm">Aquatic plants flourish, filtering nutrients and creating a natural, beautiful landscape.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[600px] rounded-sm overflow-hidden shadow-2xl">
              <img
                src={images.about}
                alt="Living water ecosystem"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Cycle */}
      <section className="py-24 px-6 lg:px-12 bg-stone-800 text-white fade-in-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-12 tracking-tight">
            The Natural Cycle
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            <div>
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sun className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-light mb-4">Day</h3>
              <p className="text-stone-300 text-sm leading-relaxed">
                Plants photosynthesize, producing oxygen. Fish are active, interacting with the environment. Water circulates, maintaining balance.
              </p>
            </div>
            <div>
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Waves className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-light mb-4">Circulation</h3>
              <p className="text-stone-300 text-sm leading-relaxed">
                Pumps move water through filters and over waterfalls. Beneficial bacteria break down waste. Plants absorb nutrients.
              </p>
            </div>
            <div>
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Wind className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-light mb-4">Balance</h3>
              <p className="text-stone-300 text-sm leading-relaxed">
                The ecosystem self-regulates. Bacteria, plants, and fish work together to maintain healthy, clear water naturally.
              </p>
            </div>
          </div>
          <p className="text-lg text-stone-300 leading-relaxed">
            This is why we call it a "living" water environment—it's a dynamic, evolving ecosystem that gets better with time.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-light text-stone-800 mb-8 tracking-tight">
            Ready to Create Your Living Water Environment?
          </h2>
          <p className="text-lg text-stone-600 mb-12 leading-relaxed">
            Let's design an ecosystem pond, stream, or waterfall that brings the beauty and tranquility of nature to your backyard.
          </p>
          <Link
            to="/collaborate"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-10 py-5 rounded-sm hover:bg-stone-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Start Your Project
          </Link>
        </div>
      </section>
    </div>
  );
};
